import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  PropsInitialStatePhotoSlice,
  IPhoto,
  PropsArrPhotos,
  PropsBoolean,
  PropsRandomPhotos,
  PropsDeletePhoto,
  ICategory,
  PropsAddNewPhoto,
} from "../../types";

import { apiApp } from "../../utils/apiApp";

const shufflePhotos = (photos: IPhoto[]): IPhoto[] => {
  const shuffled = [...photos];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

const getHomeGalleryCounts = (): { total: number; recent: number } => {
  if (window.innerWidth > 768) return { total: 12, recent: 4 };
  if (window.innerWidth > 568) return { total: 8, recent: 3 };
  if (window.innerWidth >= 320) return { total: 6, recent: 2 };
  return { total: 6, recent: 2 };
};

const compareNewestPhotos = (firstPhoto: IPhoto, secondPhoto: IPhoto): number => {
  const firstDate = Date.parse(firstPhoto.createdAt);
  const secondDate = Date.parse(secondPhoto.createdAt);

  if (!Number.isNaN(firstDate) && !Number.isNaN(secondDate) && firstDate !== secondDate) {
    return secondDate - firstDate;
  }

  return secondPhoto.order - firstPhoto.order;
};

export const fetchPhotos = createAsyncThunk(
  "photo/fetchPhotos",
  async (arg: { type: string | null; order: string }, { dispatch, rejectWithValue }) => {
    try {
      const res = await apiApp().getArrPhotos(
        `${
          arg.type === null
            ? "?type=newborn&type=baby&type=family&type=maternity&type=christening"
            : `?type=${arg.type}`
        }`
      );

      const photos = arg.order === "sort" ? [...res.data].sort((a: IPhoto, b: IPhoto) => b.order - a.order) : res.data;

      if (arg.order === "random") {
        dispatch(handlerRandomPhotos({ arr: [...photos], n: photos.length }));
      } else {
        dispatch(handlerSortPhotos(photos));
      }
      return photos;
    } catch (e) {
      return rejectWithValue("Error, the photos were not loaded!");
    }
  }
);
export const getPhotoCategories = createAsyncThunk("photo/getPhotoCategories", async (_, { rejectWithValue }) => {
  try {
    const res = await apiApp().getPhotoCategories();
    return res.data;
  } catch (e) {
    return rejectWithValue("Error, photo category has not been get!");
  }
});

export const addNewPhoto = createAsyncThunk(
  "photo/addNewPhoto",
  async (data: PropsAddNewPhoto, { rejectWithValue }) => {
    try {
      const res = await apiApp().uploadPhoto(data);
      return res.data;
    } catch (e: any) {
      return rejectWithValue(e?.response?.data?.message || "The photograph could not be uploaded.");
    }
  }
);

export const updatePhotoAlt = createAsyncThunk(
  "photo/updatePhotoAlt",
  async (data: { photoId: string; alt: string }, { rejectWithValue }) => {
    try {
      const res = await apiApp().updatePhotoAlt(data.photoId, data.alt.trim());
      return res.data;
    } catch (e) {
      return rejectWithValue("The photo description could not be saved.");
    }
  }
);

export const deletePhoto: any = createAsyncThunk(
  "photo/deletePhoto",
  async (data: PropsDeletePhoto, { rejectWithValue, dispatch }) => {
    try {
      const res = await apiApp().deletePhoto(data.photoId);
      if (res) {
        dispatch(fetchPhotos({ type: data.type, order: data.path }));
      }
      return res.data;
    } catch (e) {
      return rejectWithValue("Error, the photo was not delete");
    }
  }
);

export const saveChangeSortPhotos: any = createAsyncThunk(
  "photo/saveChangeSortPhotos",
  async (newArr: IPhoto[], { rejectWithValue }) => {
    try {
      const res = apiApp().changeOrderPhoto(newArr);
      return res.data;
    } catch (e) {
      return rejectWithValue("Error, the sort was not a happen");
    }
  }
);

const initialState: PropsInitialStatePhotoSlice = {
  getPhotos: [],
  showPhotos: [],
  categoryPhotosBtn: null,
  loading: false,
  error: "",
  openModalWithImage: false,
  dataForImageModal: "",
  openChangeSortPhotos: false,
  photoCategories: [],
};

const photoSlice = createSlice({
  name: "photo",
  initialState,
  reducers: {
    handlerDataImageForModal: (state, action: { payload: string }): void => {
      state.dataForImageModal = action.payload;
    },
    handlerModalWithImage: (state, action: PropsBoolean): void => {
      state.openModalWithImage = action.payload;
    },
    handlerActiveCategoryPhotosBtn: (state, action: { payload: string | null }): void => {
      state.categoryPhotosBtn = action.payload;
    },
    handlerRandomPhotos: (state, action: PropsRandomPhotos): void => {
      const { total, recent } = getHomeGalleryCounts();
      const availablePhotos = action.payload.arr.slice(0, action.payload.n).sort(compareNewestPhotos);
      const recentPhotos = availablePhotos.slice(0, Math.min(recent, availablePhotos.length));
      const otherPhotos = shufflePhotos(availablePhotos.slice(recent)).slice(
        0,
        Math.max(0, total - recentPhotos.length)
      );

      state.showPhotos = shufflePhotos([...recentPhotos, ...otherPhotos]);
    },
    handlerSortPhotos: (state, action: PropsArrPhotos) => {
      const sortArr = [...action.payload].sort((a: IPhoto, b: IPhoto) => b.order - a.order);
      // sortArr.sort((a: any, b: any) => b.order - a.order);

      const countSortPhotos = (arrItem: IPhoto[]): IPhoto[] => {
        if (window.innerWidth >= 1025) {
          return arrItem.slice(0, 16);
        }
        if (window.innerWidth > 768) {
          return arrItem.slice(0, 12);
        }
        if (window.innerWidth > 568) {
          return arrItem.slice(0, 8);
        }
        if (window.innerWidth >= 320) {
          return arrItem.slice(0, 6);
        }
        return arrItem;
      };
      state.showPhotos = countSortPhotos(sortArr);
    },

    handlerShowAddPhotos: (state, action: PropsArrPhotos) => {
      state.showPhotos = action.payload;
    },
    handlerOpenChangeSortPhotos: (state, action: { payload: boolean }) => {
      state.openChangeSortPhotos = action.payload;
    },
    changeOrderPhoto: (state, action: { payload: { id: string; duration: string } }) => {
      const arr = state.showPhotos;
      for (let i = 0; i < arr.length; i++) {
        if (arr[i]._id === action.payload.id) {
          let tmp;
          if (action.payload.duration.includes("down") && i !== arr.length - 1) {
            tmp = arr[i];
            arr[i] = arr[i + 1];
            arr[i + 1] = tmp;
            break;
          }
          if (action.payload.duration.includes("up") && i !== 0) {
            tmp = arr[i];
            arr[i] = arr[i - 1];
            arr[i - 1] = tmp;
            break;
          }
        }
      }
      state.showPhotos = arr;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPhotos.pending, (state): void => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(fetchPhotos.fulfilled, (state, action: { payload: IPhoto[] }): void => {
      state.getPhotos = action.payload;
      state.error = "";
      state.loading = false;
    });
    builder.addCase(fetchPhotos.rejected, (state, action: { payload: any }): void => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(getPhotoCategories.fulfilled, (state, action: { payload: ICategory[] }) => {
      state.photoCategories = action.payload;
    });
    builder.addCase(addNewPhoto.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(addNewPhoto.fulfilled, (state) => {
      state.loading = false;
      state.error = "";
    });
    builder.addCase(addNewPhoto.rejected, (state, action) => {
      state.loading = false;
      state.error = typeof action.payload === "string" ? action.payload : "The photograph could not be uploaded.";
    });
    builder.addCase(updatePhotoAlt.fulfilled, (state, action: { payload: IPhoto }) => {
      state.getPhotos = state.getPhotos.map((photo) => (photo._id === action.payload._id ? action.payload : photo));
      state.showPhotos = state.showPhotos.map((photo) => (photo._id === action.payload._id ? action.payload : photo));
    });
  },
});

export const {
  handlerRandomPhotos,
  handlerActiveCategoryPhotosBtn,
  handlerModalWithImage,
  handlerDataImageForModal,
  handlerSortPhotos,
  handlerShowAddPhotos,
  changeOrderPhoto,
  handlerOpenChangeSortPhotos,
} = photoSlice.actions;
export default photoSlice.reducer;
